import { useState } from 'react';
import { FiBriefcase, FiCheck, FiChevronRight, FiEdit2, FiGrid, FiPlus, FiSettings, FiTrash2, FiUsers, FiX } from 'react-icons/fi';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts';
import { api } from '../../services/api';
import styles from './SelectFilial.module.css';
import { limiteDeFiliais } from '../../utils/limites';

type ModalMode = 'nova-filial' | 'gerenciar' | 'editar-filial' | 'usuarios' | 'confirmar-delete';

export function SelectFilial() {
    // `filiais` sao os negocios da empresa ativa; outra empresa se escolhe no topo.
    const {
        filiais, filialAtiva, selecionarFilial, isLoadingFiliais, isAdmin, carregarFiliais, user, currentPlan,
        empresaAtiva, souDonoDaEmpresaAtiva, hasPermission,
    } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const origem = (location.state as { from?: string } | null)?.from;
    const [selecionando, setSelecionando] = useState<string | null>(null);

    const [modalMode, setModalMode] = useState<ModalMode | null>(null);
    const [editingFilial, setEditingFilial] = useState<any | null>(null);
    const [gerenciarFilial, setGerenciarFilial] = useState<any | null>(null);
    const [usuariosFilial, setUsuariosFilial] = useState<any[]>([]);
    const [saving, setSaving] = useState(false);
    const [deletingFilial, setDeletingFilial] = useState<any | null>(null);
    const [actioning, setActioning] = useState(false);
    const [error, setError] = useState('');

    const [filialForm, setFilialForm] = useState({ name: '', cnpj: '' });

    const fetchUsuarios = async (filialId: string) => {
        try {
            const data = await api.getUsuariosDaFilial(filialId);
            setUsuariosFilial(data);
        } catch {
            setUsuariosFilial([]);
        }
    };

    const handleSelecionar = async (filialId: string) => {
        setSelecionando(filialId);
        try {
            await selecionarFilial(filialId);
            navigate(origem && !origem.startsWith('/select-filial') ? origem : '/home', { replace: true });
        } finally {
            setSelecionando(null);
        }
    };

    const openNovaFilial = () => {
        setFilialForm({ name: '', cnpj: '' });
        setEditingFilial(null);
        setError('');
        setModalMode('nova-filial');
    };

    const openGerenciar = () => {
        setError('');
        setModalMode('gerenciar');
    };

    const openEditarFilial = (filial: any) => {
        setEditingFilial(filial);
        setFilialForm({ name: filial.name, cnpj: filial.cnpj ?? '' });
        setError('');
        setModalMode('editar-filial');
    };

    const openUsuarios = async (filial: any) => {
        setGerenciarFilial(filial);
        setModalMode('usuarios');
        await fetchUsuarios(filial.id);
    };

    const handleDeletar = async () => {
        if (!deletingFilial) return;
        setActioning(true);
        try {
                console.log('dentro do try, modalMode:', modalMode);

            await api.deletarFilial(deletingFilial.id);
            setModalMode('gerenciar');
            setDeletingFilial(null);
            await carregarFiliais();
        } catch (e: any) {
            setError(e.message || 'Erro ao deletar');
        } finally {
            setActioning(false);
        }
    };

    const handleSalvarFilial = async () => {
    if (!filialForm.name.trim()) { setError('Nome é obrigatório'); return; }
    const empresaId = filiais[0]?.empresaId;
    if (!empresaId) { setError('Erro interno'); return; }
    setSaving(true);
    setError('');
    try {
        if (modalMode === 'editar-filial' && editingFilial) {
            await api.updateFilial(editingFilial.id, { name: filialForm.name, cnpj: filialForm.cnpj || undefined });
        } else {
        await api.createFilial({ name: filialForm.name, cnpj: filialForm.cnpj || undefined, empresaId, adminUserId: user?.id });  }
        setModalMode(null);
        await carregarFiliais();
    } catch (e: any) {
        setError(e.message || 'Erro ao salvar');
    } finally {
        setSaving(false);
    }
};
    const porEmpresa = filiais.reduce((acc, filial) => {
        const key = filial.empresaId;
        if (!acc[key]) acc[key] = { empresa: filial.empresa, filiais: [] };
        acc[key].filiais.push(filial);
        return acc;
    }, {} as Record<string, { empresa: { id: string; name: string }; filiais: typeof filiais }>);

    // Mesma regra do backend (assertPodeGerirFiliais): dono ou permissao
    // Filiais/Empresas na empresa ativa. O papel na filial nao vale mais.
    const podeGerenciarFiliais = isAdmin || souDonoDaEmpresaAtiva || hasPermission('FILIAIS_EMPRESAS', 'EDIT');

    // O negocio novo entra na empresa ativa, entao o teto se conta nela e pelo
    // plano do dono dela. "Todos os negocios" nao entra: e visao, nao cadastro.
    const empresaDestinoId = filiais[0]?.empresaId;
    const filiaisNaEmpresa = filiais.filter(f => f.empresaId === empresaDestinoId).length;
    const limiteFiliais = limiteDeFiliais(empresaAtiva ? empresaAtiva.plano?.maxAccounts : currentPlan?.maxAccounts);
    const limiteFiliaisAtingido = filiaisNaEmpresa >= limiteFiliais;

    const isTodasAtiva = filialAtiva?.id === 'all';
    const isLoadingTodas = selecionando === 'all';

    const roleLabel = (role: string) => ({ ADMIN: 'Admin', EDITOR: 'Editor', VIEWER: 'Visualizador' }[role] ?? role);

    return (
        <div className={styles.container}>
            <div className={styles.card}>
                <div className={styles.header}>
                    <div className={styles.iconWrapper}>
                        <FiBriefcase size={24} />
                    </div>
                    <h1 className={styles.title}>Selecionar Negócio</h1>
                    <p className={styles.subtitle}>Escolha com qual negócio deseja trabalhar</p>
                </div>

                {isLoadingFiliais ? (
                    <div className={styles.loading}>Carregando negócios...</div>
                ) : filiais.length === 0 ? (
                    <div className={styles.empty}>Nenhum negócio disponível.</div>
                ) : (
                    <div className={styles.list}>
                        <div className={styles.empresaGroup}>
                            <div className={styles.empresaLabel}>Visão Geral (apenas visualização)</div>
                            <button
                                className={`${styles.filialItem} ${styles.todasFiliais} ${isTodasAtiva ? styles.ativa : ''}`}
                                onClick={() => handleSelecionar('all')}
                                disabled={!!selecionando}
                            >
                                <div className={`${styles.filialIcon} ${styles.filialIconTodas}`}>
                                    <FiGrid size={16} />
                                </div>
                                <span className={styles.todasTexto}>
                                    <span className={styles.filialName}>Todos os negócios</span>
                                    <span className={styles.todasAviso}>
                                        Mostra os números de todos os negócios juntos. Não lança receitas nem despesas.
                                    </span>
                                </span>
                                {isLoadingTodas ? (
                                    <span className={styles.loadingDot} />
                                ) : isTodasAtiva ? (
                                    <FiCheck size={16} className={styles.checkIcon} />
                                ) : (
                                    <FiChevronRight size={16} className={styles.chevron} />
                                )}
                            </button>
                        </div>

                        {Object.values(porEmpresa).map(({ empresa, filiais: filiaisEmpresa }) => (
                            <div key={empresa.id} className={styles.empresaGroup}>
                                <div className={styles.empresaLabel}>{empresa.name}</div>
                                {filiaisEmpresa.map(filial => {
                                    const isAtiva = filialAtiva?.id === filial.id;
                                    const isLoading = selecionando === filial.id;
                                    return (
                                        <div key={filial.id} className={styles.filialRow}>
                                            <button
                                                className={`${styles.filialItem} ${isAtiva ? styles.ativa : ''}`}
                                                onClick={() => handleSelecionar(filial.id)}
                                                disabled={!!selecionando}
                                            >
                                                <div className={styles.filialIcon}>
                                                    <FiBriefcase size={16} />
                                                </div>
                                                <span className={styles.filialName} title={filial.name}>{filial.name}</span>
                                                {isLoading ? (
                                                    <span className={styles.loadingDot} />
                                                ) : isAtiva ? (
                                                    <FiCheck size={16} className={styles.checkIcon} />
                                                ) : (
                                                    <FiChevronRight size={16} className={styles.chevron} />
                                                )}
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        ))}
                    </div>
                )}

                {podeGerenciarFiliais && (
                    <div className={styles.adminActions}>
                        <button
                            className={styles.adminBtn}
                            onClick={openNovaFilial}
                            disabled={limiteFiliaisAtingido}
                            title={limiteFiliaisAtingido ? `Seu plano permite até ${limiteFiliais} negócio(s)` : undefined}
                        >
                            <FiPlus size={15} /> Novo Negócio
                        </button>
                        <button className={styles.adminBtnSecondary} onClick={openGerenciar}>
                            <FiSettings size={15} /> Gerenciar Negócios
                        </button>
                    </div>
                )}

                {podeGerenciarFiliais && limiteFiliaisAtingido && (
                    <p className={styles.limiteAviso}>
                        Seu plano permite até {limiteFiliais} negócio(s) e você já chegou lá. Exclua um ou mude de plano para cadastrar outro.
                    </p>
                )}
            </div>

            {/* Modal Nova Filial / Editar Filial */}
            {(modalMode === 'nova-filial' || modalMode === 'editar-filial') && (
                <div className={styles.modalOverlay} onClick={() => setModalMode(null)}>
                    <div className={styles.modal} onClick={e => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h2 className={styles.modalTitle}>
                                {modalMode === 'editar-filial' ? 'Editar Negócio' : 'Novo Negócio'}
                            </h2>
                            <button className={styles.modalClose} onClick={() => setModalMode(null)}>
                                <FiX size={18} />
                            </button>
                        </div>
                        <div className={styles.modalBody}>
                            <div className={styles.formGroup}>
                                <label>Nome do Negócio</label>
                                <input
                                    className={styles.input}
                                    placeholder="Ex: Loja São Paulo"
                                    value={filialForm.name}
                                    onChange={e => setFilialForm(f => ({ ...f, name: e.target.value }))}
                                />
                            </div>
                            <div className={styles.formGroup}>
                                <label>CNPJ (opcional)</label>
                                <input
                                    className={styles.input}
                                    placeholder="00.000.000/0000-00"
                                    value={filialForm.cnpj}
                                    onChange={e => setFilialForm(f => ({ ...f, cnpj: e.target.value }))}
                                />
                            </div>
                            {error && <div className={styles.error}>{error}</div>}
                        </div>
                        <div className={styles.modalFooter}>
                            <button className={styles.btnCancel} onClick={() => setModalMode(null)}>Cancelar</button>
                            <button className={styles.btnSave} onClick={handleSalvarFilial} disabled={saving}>
                                {saving ? 'Salvando...' : 'Salvar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal Gerenciar Filiais */}
            {modalMode === 'gerenciar' && (
                <div className={styles.modalOverlay} onClick={() => setModalMode(null)}>
                    <div className={styles.modal} onClick={e => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h2 className={styles.modalTitle}>Gerenciar Negócios</h2>
                            <button className={styles.modalClose} onClick={() => setModalMode(null)}>
                                <FiX size={18} />
                            </button>
                        </div>
                        <div className={styles.modalBody}>
                            {filiais.length === 0 ? (
                                <div className={styles.empty}>Nenhum negócio cadastrado.</div>
                            ) : (
                                <div className={styles.gerenciarList}>
                                    {filiais.map(filial => (
                                        <div key={filial.id} className={styles.gerenciarItem}>
                                            <div className={styles.gerenciarInfo}>
                                                <div className={styles.gerenciarIcon}>
                                                    <FiBriefcase size={15} />
                                                </div>
                                                <div>
                                                    <div className={styles.gerenciarName} title={filial.name}>{filial.name}</div>
                                                </div>
                                            </div>
                                            <div className={styles.gerenciarActions}>
                                                <button className={styles.gerenciarBtn} title="Editar negócio" onClick={() => openEditarFilial(filial)}>
                                                    <FiEdit2 size={14} />
                                                </button>
                                                <button className={styles.gerenciarBtn} title="Gerenciar usuários" onClick={() => openUsuarios(filial)}>
                                                    <FiUsers size={14} />
                                                </button>
                                                <button
                                                    className={`${styles.gerenciarBtn} ${styles.gerenciarBtnDanger}`}
                                                    title="Excluir negócio"
                                                    onClick={() => { setDeletingFilial(filial); setModalMode('confirmar-delete'); }}
                                                >
                                                    <FiTrash2 size={14} />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {error && <div className={styles.error}>{error}</div>}
                        </div>
                        <div className={styles.modalFooter}>
                            <button className={styles.btnSave} onClick={() => setModalMode(null)}>Fechar</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal Confirmar Delete */}
            {modalMode === 'confirmar-delete' && deletingFilial && (
                <div className={styles.modalOverlay} onClick={() => setModalMode('gerenciar')}>
                    <div className={styles.modal} onClick={e => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h2 className={styles.modalTitle}>Excluir Negócio</h2>
                            <button className={styles.modalClose} onClick={() => setModalMode('gerenciar')}>
                                <FiX size={18} />
                            </button>
                        </div>
                        <div className={styles.modalBody}>
                            <div className={styles.confirmText}>
                                Tem certeza que deseja excluir <strong>{deletingFilial.name}</strong>?
                                <br /><br />
                                <span className={styles.confirmWarning}>
                                    Esta ação é irreversível e removerá todos os dados vinculados a este negócio.
                                </span>
                            </div>
                            {error && <div className={styles.error}>{error}</div>}
                        </div>
                        <div className={styles.modalFooter}>
                            <button className={styles.btnCancel} onClick={() => setModalMode('gerenciar')}>Cancelar</button>
                            <button className={styles.btnDanger} onClick={handleDeletar} disabled={actioning}>
                                {actioning ? 'Excluindo...' : 'Excluir'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal Usuários da Filial */}
            {modalMode === 'usuarios' && gerenciarFilial && (
                <div className={styles.modalOverlay} onClick={() => setModalMode('gerenciar')}>
                    <div className={styles.modal} onClick={e => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <div>
                                <h2 className={styles.modalTitle}>Usuários — {gerenciarFilial.name}</h2>
                                <p style={{ fontSize: '0.75rem', color: 'var(--color-text-tertiary)', marginTop: 2 }}>
                                    Gerencie quem tem acesso a este negócio
                                </p>
                            </div>
                            <button className={styles.modalClose} onClick={() => setModalMode('gerenciar')}>
                                <FiX size={18} />
                            </button>
                        </div>
                        <div className={styles.modalBody}>
                            {usuariosFilial.length === 0 ? (
                                <div className={styles.empty}>Nenhum usuário associado.</div>
                            ) : (
                                <div className={styles.gerenciarList}>
                                    {usuariosFilial.map((uf: any) => (
                                        <div key={uf.user.id} className={styles.gerenciarItem}>
                                            <div className={styles.gerenciarInfo}>
                                                <div className={styles.gerenciarIcon}>
                                                    <FiUsers size={15} />
                                                </div>
                                                <div>
                                                    <div className={styles.gerenciarName}>{uf.user.name}</div>
                                                    <div className={styles.gerenciarEmpresa}>{uf.user.email}</div>
                                                </div>
                                            </div>
                                            <span className={styles.roleBadge}>{roleLabel(uf.role)}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                            <div className={styles.usuariosNote}>
                                <span>✦ Gerenciamento completo de acesso em breve</span>
                            </div>
                        </div>
                        <div className={styles.modalFooter}>
                            <button className={styles.btnCancel} onClick={() => setModalMode('gerenciar')}>Voltar</button>
                            <button className={styles.btnSave} onClick={() => setModalMode('gerenciar')}>Fechar</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}